import React from 'react';
import axios from 'axios';
import FAQExpandable from './FAQExpandable';
import { NavLink } from 'react-router-dom';
import './index.scss';
import { Section } from '../../../components/shared/Section';
import TransitionIn from '../../../components/shared/TransitionIn';

const FrequentlyAskedQuestions = props => {

    const heading = "FAQs";

    const body = (
        <article className="faq">
            <h2>STATEMENTS AND PAYMENTS:</h2>
            <FAQExpandable
                heading="Is my loan payment required to be made via recurring ACH?" content={
                    <>
                        <p>Lima One Capital requires that all payments for newly closed loans are made via recurring ACH. Please refer to the <NavLink to={`${process.env.REACT_APP_SESSION_URL}/fee-schedule`} >online fee schedule</NavLink> for more information.</p>
                    </>
                } />

            <FAQExpandable
                heading={'How can I send a payment by wire?'}
                content={
                    <>
                        <p>To send payments via wire, please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> for wiring instructions. Make sure to include the number and property address with the wire transfer.</p>
                    </>
                } />


            <FAQExpandable
                heading={'How can I access my monthly billing statements?'}
                content={
                    <>
                        <p>
                            <ul>
                                <li>Select <strong>Make a Payment</strong></li>
                                <li>Select <strong>View Bill History</strong></li>
                            </ul>
                        </p>
                    </>
                } />


            <FAQExpandable
                heading={'How do I establish AutoPay, or update the banking information on my existing AutoPay schedule?'}
                content={
                    <>
                        <p>
                            To authorize ACH payments, please complete the <a href='https://bit.ly/LimaOne-ACH' target='_blank'>ACH Authorization Form</a>. Please fill out the form, scan, and send back at your earliest convenience. If you have additional questions, please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a>.</p>
                    </>
                } />

            <h2>CONSTRUCTION DRAWS:</h2>

            <FAQExpandable
                heading={'How do I request a construction draw?'}
                content={
                    <>
                        <p>When you’re ready to request a draw for reimbursement, follow these steps via the Lima One portal:</p>
                        <ul>
                            <li><strong>Log In:</strong><br/>
                                Select the property address under “Construction.” Here, you can view the remaining construction rehab funds, previous draw items, and approved budget line items.
                            </li>
                            <li><strong>Enter Request:</strong><br/>
                                Input the desired amounts for each line item and select “Request Draw.” On the next screen, provide the inspection date range, lockbox access code, and choose the bank account for the funds to be deposited. Then select “Submit.” For the first draw request, select “Add New Account” to link your bank account.
                            </li>
                            <li><strong>Inspection and Funds Transfer:</strong><br/>
                                Once received, our Construction Management Team will arrange a third-party inspection to verify the completion of requested items. If the inspection report is satisfactory, funds will be wired directly to the designated bank account. Please note, additional documentation may be required to approve draw requests.
                            </li>
                        </ul>
                    </>
                } />

            <FAQExpandable
                heading={'What other kinds of documents will I need to submit for draw requests?'}
                content={
                    <>
                        <p>To ensure a quick turnaround, please upload supporting documents to our secure online account portal prior to submitting the request. Examples of documentation include:</p>
                        <ul>
                            <li>
                                Permits
                            </li>
                            <li>
                                Stamped/city-approved plans
                            </li>
                            <li>
                                General Contractor signed lien waivers
                            </li>
                            <li>
                                Paid invoices (required when requesting any "Contingency" funds)
                            </li>
                        </ul>
                    </>
                } />

                
            <FAQExpandable
                heading={'How long does it take for me to receive my construction draw funds?'}
                content={
                    <>
                        <p>Typically 3-5 business days after the initial request has been received.</p>
                    </>
                } />

            <FAQExpandable
                heading={'Is there a fee associated with each construction draw?'}
                content={
                    <>
                        <p>The inspection fee is $275 for each request. For loans with multiple properties, the fee is $175 per property inspected. Draw fees may change during the term of the loan. To avoid any inconvenience, this fee is deducted from the approved draw amount.</p>
                    </>
                } />

            <FAQExpandable
                heading={'With construction draws, can I request funds in advance for materials?'}
                content={
                    <>
                        <p>In accordance with the loan documents and Lima One Capital’s reimbursement policy, funds will only be released for completed work. However, we can release partial funds based on the percentage of work completed.</p>
                    </>
                } />

            <FAQExpandable
                heading={'What is the Lockbox Policy?'}
                content={
                    <>
                        <p>Please be aware that Lima One Capital has a lockbox policy that requires lockboxes to be placed on-site for all projects. This is to ensure that we can access the property at any time for inspections (when necessary). The lockbox code can be provided when the draw is submitted, and any updates can be communicated directly to the construction analyst. If an inspector is unable to access the interior of the property due to the absence of a lockbox, additional “no access” fees may apply.</p>
                    </>
                } />


            <h2>TAXES & INSURANCE:</h2>

            <FAQExpandable
                heading={'How do I know if my loan is escrowed?'}
                content={
                    <>
                        <p>Please review the loan documents to determine if the loan is escrowed. For assistance in finding this information, please contact me. Please note, all Rental30 loans are escrowed, while most Construction and Bridge loans are not.</p>
                        <ul>
                            <li>
                                <strong>Escrowed Loans:</strong>
                                A portion of the monthly payment is deposited into an escrow account to cover property taxes and insurance.
                            </li>
                            <li>
                                <strong>Non-Escrowed Loans:</strong>
                                You are responsible for directly paying property taxes and insurance.
                            </li>
                        </ul>
                        <p>Please note, property taxes and insurance tend to increase year-over-year, and your payment may change as a result.</p>
                    </>
                } />

            <FAQExpandable
                heading={'What is the required mortgagee clause for insurance policies?'}
                content={
                    <>
                        <p>Listed below is the mortgagee clause that all insurance policies are required to have:</p>
                        <br/>
                        <p>
                            <center>
                                Lima One Capital ISAOA/ATIMA<br/>
                                C/O Lee & Mason Financial Services, Inc.<br/>
                                P.O. Box 8485<br/>
                                Reston, VA 20195
                            </center>
                        </p>
                    </>
                } />

            <FAQExpandable
                heading={'What if I change insurance companies while the loan is still active?'}
                content={
                    <>
                        <ul>
                            <li>
                                <strong>Rental30 (Escrowed):</strong>
                                If you change insurance companies, the policy must insure the lesser of i) the full Replacement Value or ii) the allocated Unpaid Principal Balance allocated to each insured property. Coverage must protect against loss or Damage from fire, lightening, and extended coverage endorsement, with wind, civil commotion, smoke, hail, and damages cause by aircraft, vehicle, or explosion. If property is vacant at closing, the insurance accord or declarations must identify there are no vacancy limitations/provisions.
                            </li>
                            <li>
                                <strong>FixNFlip, New Construction & Bridge+ (Non-Escrowed):</strong>
                                If you change insurance companies, the policy must insure the lesser of i) 100% insurable value or ii) the allocated Unpaid Principal Balance allocated to each insured property. Coverage must protect against loss or Damage from fire, lightening, and extended coverage endorsement, with wind, civil commotion, smoke, hail, and damages cause by aircraft, vehicle, or explosion.
                            </li>
                        </ul>
                        <p>Please have a copy of the new or renewed policies faxed to 518.863.7400 or upload them to our insurance tracking website, <a href='https://www.ValidateMyInsurance.com' target='_blank'>www.ValidateMyInsurance.com</a>.</p>
                    </>
                } />

            <FAQExpandable
                heading={'How will my annual insurance policy be paid?'}
                content={
                    <>
                        <ul>
                            <li>
                                <strong>Escrowed loans:</strong>
                                All insurance policy invoices must be billed to Lima One Capital (see the mortgagee clause address above) for the full annual premium. If invoices are sent to your home address, please fax them to 518.863.7400 or upload them on our insurance tracking website, <a href='https://www.ValidateMyInsurance.com' target='_blank'>www.ValidateMyInsurance.com</a>. Payments for insurance policies will be disbursed from the escrow account.
                            </li>
                            <li>
                                <strong>Non-escrowed loans:</strong>
                                Lima One Capital tracks insurance coverage to ensure it meets the requirements specified in the loan documents. You are responsible for paying the annual insurance premiums and providing Lima One Capital with proof of coverage (including evidence of insurance and a paid receipt) to avoid forced-place insurance or penalties. Please have a copy of new or renewed policies faxed to 518.863.7400 or upload them on our insurance tracking website, <a href='https://www.ValidateMyInsurance.com' target='_blank'>www.ValidateMyInsurance.com</a>.
                            </li>
                        </ul>
                    </>
                } />

            <FAQExpandable
                heading={'How will property taxes be paid?'}
                content={
                    <>
                        <ul>
                            <li>
                                <strong>Escrowed loans:</strong>
                                Property tax payments will be disbursed from the escrow account. Note that supplemental bills, additional tax bills, utility bills, and Homeowner Association fees are the property owner’s responsibility and will not be paid from escrow.
                            </li>
                            <li>
                                <strong>Non-escrowed loans:</strong>
                                 You are responsible for keeping all property taxes current in accordance with the loan agreement. If insurance coverage lapses or taxes become delinquent, Lima One Capital reserves the right to pay these costs on your behalf and add them to the loan balance.
                            </li>
                        </ul>                    
                    </>
                } />

            <FAQExpandable
                heading={'What is an escrow analysis and when will it be completed?'}
                content={
                    <>
                        <p>Each year, we will review the escrow account to adjust the monthly payment and required balance based on the prior year’s payments. Generally, the review takes place on the anniversary of the closing date and takes effect at least 45 days later.</p>
                    </>
                } />

            <h2>LOAN MATURITY:</h2>

            <FAQExpandable
                heading={'How do I request a payoff letter?'}
                content={
                    <>
                        <p>Please request a payoff letter by completing a <NavLink target="_blank" to={`${process.env.REACT_APP_SESSION_URL}/servicing-requests/payoff`}>Payoff Request Form</NavLink>.</p>
                        <p>Once our Payoff Department receives the request, an encrypted email will be sent with the payoff statement and wiring instructions within 2-3 business days following the day of the request.</p>
                    </>
                } />

            <FAQExpandable
                heading={'Will I pay a pre-payment premium (PPP) if I pay my loan off before its maturity?'}
                content={
                    <>
                        <p>To determine if the loan includes a pre-payment premium, please review the loan documents. In general, there is no PPP in connection with loans with terms shorter than 36 months.</p>
                    </>
                } />

            <FAQExpandable
                heading={'What is the minimum to release one property from a portfolio loan?'}
                content={
                    <>
                        <p>Release Pricing is the amount required to remove a single property from a loan that covers multiple properties. It helps ensure the loan remains secure after one property is paid off. The amount isn’t a fee or penalty—it’s a standard part of the loan structure. The exact pricing is outlined in your loan documents.</p>
                    </>
                } />

            <FAQExpandable
                heading={'What is a loan extension and how do I apply for one?'}
                content={
                    <>
                        <p>A loan extension for a construction loan provides additional time to complete the project if required. An extension will allow more time to finish construction and to exit prior to the extended maturity date. </p>
                    
                        <p>Please complete an <NavLink target="_blank" to={`${process.env.REACT_APP_SESSION_URL}/servicing-requests/extension`}>Extension Application</NavLink> (no later than 45 days prior to the maturity date).</p>

                        <p>Please note, extensions are granted at Lima One Capital’s discretion and are not guaranteed.</p>
                    </>
                } />

            <FAQExpandable
                heading={'I want to refinance my loan, who do I need to speak with?'}
                content={
                    <>
                        <p>Visit the <a href='https://app.limaone.com/auth' target='_blank'>Customer Portal</a> to submit an application or reach out to your Sales Representative.</p>
                    </>
                } />

            <FAQExpandable
                heading={'How do I obtain a VOM (Verification of Mortgage)?'}
                content={
                    <>
                        <p>Please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> to obtain a current VOM (Verification of Mortgage).</p>
                    </>
                } />

            <FAQExpandable
                heading={'What is the process for a CEMA (Consolidation, Extension, and Modification Agreement) refinance in NY?'}
                content={
                    <>
                        <p>For CEMA refinances, please submit a request to <a href='mailto:cema@limaone.com'>cema@limaone.com</a>. Our post-closing department will provide a CEMA form and collect a non-refundable fee of $500, payable upon form submission. Please note that Lima One Capital is not responsible for meeting closing deadlines. Additionally, Lima One Capital reserves the right to decline a CEMA refinance with another lender. Please note that it may take 4-6 weeks to process a CEMA request, so please submit any CEMA request as soon as possible. </p>
                    </>
                } />

            <h2>GENERAL INFORMATION:</h2>
            
            <FAQExpandable
                heading={'Will my interest rate change?'}
                content={
                    <>
                        <p>
                            To determine if the interest rate will change, please review the loan documents. Please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> for assistance in finding this information or if you have questions about whether the loan is an ARM (Adjustable-Rate Mortgage).    
                        </p>
                    </>
                } />

            <FAQExpandable
                heading={'How do I update my mailing address or contact information?'}
                content={
                    <>
                        <p>Please contact our dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> to update the mailing address or contact information.</p>
                    </>
                } />

            <FAQExpandable
                heading={'How will I receive my 1098?'}
                content={
                    <>
                        <p>
                            Replicate the loan is eligible for an IRS Form1098, a copy will be sent to the mailing address on file. Please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> to obtain a digital copy.
                        </p>
                        <p>
                            Please note that business entities classified as multi-member LLCs, Inc., corporations, or partnerships will not receive a 1098 per IRS guidelines but can request a year-end statement. Please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a> to request a year-end statement.
                        </p>
                    </>
                } />

            <FAQExpandable
                heading={'How do I submit a loss claim?'}
                content={
                    <>
                        <p>Please submit a loss claim by submitting the <NavLink target="_blank" to={`${process.env.REACT_APP_SESSION_URL}/servicing-requests/loss-draft`}>Loss Claim Application</NavLink>. For additional details on the loss claim process, please visit the <a href='https://bit.ly/LimaOne-LossClaim' target='_blank'>Loss Claim Packet</a>.</p>
                    </>
                } />

            <FAQExpandable
                heading={'What are CapEx (Capital Expenditures) funds?'}
                content={
                    <>
                        <p>CapEx, or Capital Expenditures, refers to funds used for general property maintenance. CapEx is applicable only with specific loan types and must be established before closing.</p>
                    </>
                } />

            <FAQExpandable
                heading={'How do I submit a CapEx (Capital Expenditures) funds request?'}
                content={
                    <>
                        <p>Please complete the <NavLink target="_blank" to={`${process.env.REACT_APP_SESSION_URL}/servicing-requests/capex`}>CapEx Request Form</NavLink>.</p>
                        <p>If you have additional questions, please contact your dedicated Servicing Specialist or our Servicing Department at <a href='mailto:servicing@limaone.com'>servicing@limaone.com</a>!</p>
                    </>
                } />

        </article>
    )

    return (
        <TransitionIn content={ <Section heading={heading} body={body} /> } />
    )

}

export default FrequentlyAskedQuestions